/**
 * Country-based Custom Data Handler
 *
 * This script automatically populates elements with .custom_data class
 * based on the user's localization.country setting.
 */

(function() {
  'use strict';

  // Hardcoded JSON data structure
  // Format: { countryCode: { key: value } }
  // Only real data here. The agency's example keys ('shipping' with made-up free
  // shipping, 'phone' with fake numbers, 'support_hours') were removed on purpose:
  // there is no free shipping and no phone line. Don't add them back.
  const COUNTRY_DATA = {
    // 24-72 hours - Portugal, Spain
    'PT': {
      'delivery': '24-72 hours',
      'currency': 'EUR',
    },
    'ES': {
      'delivery': '24-72 hours',
      'currency': 'EUR',
    },
    // 3-4 days - Austria
    'AT': {
      'delivery': '3-4 days',
      'currency': 'EUR',
    },
    // 3-5 days - Czechia
    'CZ': {
      'delivery': '3-5 days',
      'currency': 'CZK',
    },
    // 3-6 days - Poland, Romania
    'PL': {
      'delivery': '3-6 days',
      'currency': 'PLN',
    },
    'RO': {
      'delivery': '3-6 days',
      'currency': 'RON',
    },
    // 4-10 days - Denmark, Estonia, Norway, Sweden
    'DK': {
      'delivery': '4-10 days',
      'currency': 'DKK',
    },
    'EE': {
      'delivery': '4-10 days',
      'currency': 'EUR',
    },
    'NO': {
      'delivery': '4-10 days',
      'currency': 'NOK',
    },
    'SE': {
      'delivery': '4-10 days',
      'currency': 'SEK',
    },
    // 4-7 business days - Greece, Slovakia, Switzerland
    'GR': {
      'delivery': '4-7 business days',
      'currency': 'EUR',
    },
    'SK': {
      'delivery': '4-7 business days',
      'currency': 'EUR',
    },
    'CH': {
      'delivery': '4-7 business days',
      'currency': 'CHF',
    },
    // 4-7 days - Croatia, Hungary, Latvia, Lithuania
    'HR': {
      'delivery': '4-7 days',
      'currency': 'EUR',
    },
    'HU': {
      'delivery': '4-7 days',
      'currency': 'HUF',
    },
    'LV': {
      'delivery': '4-7 days',
      'currency': 'EUR',
    },
    'LT': {
      'delivery': '4-7 days',
      'currency': 'EUR',
    },
    // 48-96 hours - Belgium, France, Germany, Italy, Netherlands
    'BE': {
      'delivery': '48-96 hours',
      'currency': 'EUR',
    },
    'FR': {
      'delivery': '48-96 hours',
      'currency': 'EUR',
    },
    'DE': {
      'delivery': '48-96 hours',
      'currency': 'EUR',
    },
    'IT': {
      'delivery': '48-96 hours',
      'currency': 'EUR',
    },
    'NL': {
      'delivery': '48-96 hours',
      'currency': 'EUR',
    },
    // Other countries
    'US': {
      'delivery': '2-5 business days',
      'currency': 'USD',
    },
    'GB': {
      'delivery': '3-7 business days',
      'currency': 'GBP',
    },
    'CA': {
      'delivery': '3-7 business days',
      'currency': 'CAD',
    },
    'AU': {
      'delivery': '5-10 business days',
      'currency': 'AUD',
    },
    // Default fallback
    'DEFAULT': {
      'delivery': '5-10 business days',
      'currency': 'USD',
    }
  };

  /**
   * Get current country from Shopify localization
   * @returns {string} Country code (e.g., 'US', 'GB')
   */
  function getCurrentCountry() {
    // Try to get from Shopify localization object
    if (window.Shopify && window.Shopify.country) {
      return window.Shopify.country;
    }

    // Try to get from localization routes if available
    if (window.localization && window.localization.country) {
      return window.localization.country.iso_code;
    }

    // Fallback: try to parse from URL or routes
    const pathParts = window.location.pathname.split('/');
    if (pathParts.length > 1 && pathParts[1].length === 2) {
      return pathParts[1].toUpperCase();
    }

    // Default to US if nothing else works
    return 'DEFAULT';
  }

  /**
   * Get data for a specific key and country
   * @param {string} key - The data key to retrieve
   * @param {string} country - Country code
   * @returns {string} The data value or empty string
   */
  function getCountryData(key, country) {
    const countryData = COUNTRY_DATA[country] || COUNTRY_DATA['DEFAULT'];
    return countryData[key] || '';
  }

  /**
   * Replace placeholders in text content
   * Supports format: [[key_name]] or [[key_name|fallback value]]
   * @param {string} text - Text that may contain placeholders
   * @param {string} country - Country code
   * @returns {string} Text with placeholders replaced
   */
  function replacePlaceholders(text, country) {
    if (!text) return text;

    // Match [[key_name]] or [[key_name|fallback]] pattern
    const placeholderRegex = /\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g;

    return text.replace(placeholderRegex, (match, key, fallback) => {
      const data = getCountryData(key.trim(), country);

      // Return data if found, otherwise fallback, otherwise original match
      if (data) {
        return data;
      } else if (fallback) {
        return fallback.trim();
      } else {
        return match;
      }
    });
  }

  /**
   * Update all elements with .custom_data class
   */
  function updateCustomDataElements() {
    const currentCountry = getCurrentCountry();
    const elements = document.querySelectorAll('.custom_data');

    elements.forEach(element => {
      // Get the data key from data-key attribute
      const dataKey = element.dataset.key;

      if (!dataKey) {
        console.warn('Element with .custom_data has no data-key attribute:', element);
        return;
      }

      // Get the data for this key and country
      const data = getCountryData(dataKey, currentCountry);

      // Update the element's content
      if (data) {
        // Check if element has data-target attribute for specific child element
        const target = element.dataset.target;
        if (target) {
          const targetElement = element.querySelector(target);
          if (targetElement) {
            targetElement.textContent = data;
          } else {
            element.textContent = data;
          }
        } else {
          element.textContent = data;
        }
      }
    });

    console.log(`[Country Custom Data] Updated ${elements.length} .custom_data element(s) for country: ${currentCountry}`);
  }

  /**
   * Process all text nodes that may contain [[key]] placeholders
   */
  function processPlaceholders() {
    const currentCountry = getCurrentCountry();

    // Find all text nodes containing [[ ]]
    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: function(node) {
          // Only process text nodes that contain [[
          if (node.nodeValue && node.nodeValue.includes('[[')) {
            return NodeFilter.FILTER_ACCEPT;
          }
          return NodeFilter.FILTER_SKIP;
        }
      }
    );

    const textNodes = [];
    let node;
    while (node = walker.nextNode()) {
      textNodes.push(node);
    }

    // Replace placeholders in each text node
    textNodes.forEach(node => {
      node.nodeValue = replacePlaceholders(node.nodeValue, currentCountry);
    });

    console.log(`[Country Custom Data] Processed ${textNodes.length} placeholder(s) for country: ${currentCountry}`);
  }

  /**
   * Initialize the custom data handler
   */
  function init() {
    // Update on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        updateCustomDataElements();
        processPlaceholders();
      });
    } else {
      updateCustomDataElements();
      processPlaceholders();
    }

    // Also listen for Shopify section rendering events
    document.addEventListener('shopify:section:load', function() {
      updateCustomDataElements();
      processPlaceholders();
    });
    document.addEventListener('shopify:section:reorder', function() {
      updateCustomDataElements();
      processPlaceholders();
    });
  }

  // Start the script
  init();

  // Expose update function globally for manual triggers if needed
  window.updateCountryCustomData = function() {
    updateCustomDataElements();
    processPlaceholders();
  };

})();
